import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {useFreezableFilter} from 'hooks/useFreezableFilter'
import {
  getGroupedHotelDetails,
  getIsAnchorScenario,
  getIsSearchCompleted,
  getIsSearchHasResults,
  getSearchId
} from 'modules/sapiSearch/selectors'
import {getDealFreezeFilter} from 'modules/search/selectors'
import {
  hasDealFreezeEducationBannerEverShown,
  setDealFreezeEducationBannerCtaClicked,
  setDealFreezeEducationBannerShown,
  wasDealFreezeEducationBannerShownToday
} from 'utils/persistence'
import {getIsFromGha} from 'utils/trafficSource'

import {selectIsLoading} from '@vio/core/src/auth/modules/selectors'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {logger} from '@vio/core/src/utils/logging/logger'

import {
  BANNER_PLACEMENT,
  DEAL_FREEZE_EDUCATION_BANNER_EXPERIMENT
} from './constants'
import {
  trackDealFreezeEducationBannerCtaClicked,
  trackDealFreezeEducationBannerDismissed,
  trackDealFreezeEducationBannerDisplayed
} from './tracking'
import {useDealFreezeEducationBannerEligibility} from './useDealFreezeEducationBannerEligibility'
import {useIsBannerTopSlotOccupied} from './useIsBannerTopSlotOccupied'

import type {DealFreezeEducationBannerPlacement} from './constants'

const filterKeys = ['dealFreeze']

interface Impression {
  placement: DealFreezeEducationBannerPlacement
  searchId: string | undefined
  isVisible: boolean
}

interface ContextValue {
  placement: DealFreezeEducationBannerPlacement
  isTriggerable: boolean
  visiblePlacement: DealFreezeEducationBannerPlacement | undefined
  startImpression: () => void
  dismiss: () => void
  onCtaClick: () => void
}

const noop = () => undefined

const missingProviderValue: ContextValue = {
  placement: BANNER_PLACEMENT.secondary,
  isTriggerable: false,
  visiblePlacement: undefined,
  startImpression: noop,
  dismiss: noop,
  onCtaClick: noop
}

const DealFreezeEducationBannerContext = createContext<
  ContextValue | undefined
>(undefined)

export const useDealFreezeEducationBannerContext = (): ContextValue => {
  const value = useContext(DealFreezeEducationBannerContext)

  if (!value) {
    logger.warn(
      'useDealFreezeEducationBannerContext() used without <DealFreezeEducationBannerProvider>. The banner will not render.'
    )
    return missingProviderValue
  }

  return value
}

export const DealFreezeEducationBannerProvider: React.FC<{
  children: React.ReactNode
}> = ({children}) => {
  const dispatch = useDispatch()
  const toggle = useToggle()

  const isEligible = useDealFreezeEducationBannerEligibility()
  const isBannerTopSlotOccupied = useIsBannerTopSlotOccupied()
  const isAnchorSearch = useSelector(getIsAnchorScenario)
  const isFromGha = useSelector(getIsFromGha)
  const isAuthLoading = useSelector(selectIsLoading)

  const searchId = useSelector(getSearchId)
  const isFreezableFilterAvailable = useFreezableFilter()
  const isDealFreezeFilterApplied = useSelector(getDealFreezeFilter)
  const isSearchCompleted = useSelector(getIsSearchCompleted)
  const hasResults = useSelector(getIsSearchHasResults)
  const {greatDeals} = useSelector(getGroupedHotelDetails)

  const [hasEverShown] = useState(() => hasDealFreezeEducationBannerEverShown())
  const [impression, setImpression] = useState<Impression | undefined>(
    undefined
  )
  const [hasStarted, setHasStarted] = useState(false)
  // Guards a second call in the same commit, before hasStarted has re-rendered.
  const hasStartedRef = useRef(false)

  const {submitFilters} = useFiltersForm(
    filterKeys,
    'dealFreezeEducationBanner'
  )

  const placement: DealFreezeEducationBannerPlacement =
    !hasEverShown && !isBannerTopSlotOccupied && !(isFromGha && isAnchorSearch)
      ? BANNER_PLACEMENT.primary
      : BANNER_PLACEMENT.secondary

  const impressionPlacement = impression?.placement
  const isImpressionVisible = Boolean(impression?.isVisible)
  const isStaleForGha = Boolean(
    impression && isFromGha && searchId !== impression.searchId
  )

  const visiblePlacement =
    isImpressionVisible && !isStaleForGha ? impressionPlacement : undefined

  const hide = useCallback(
    () =>
      setImpression(current =>
        current ? {...current, isVisible: false} : current
      ),
    []
  )

  const startImpression = useCallback(() => {
    if (hasStartedRef.current || wasDealFreezeEducationBannerShownToday()) {
      return
    }

    hasStartedRef.current = true
    setHasStarted(true)
    setDealFreezeEducationBannerShown()

    const isEducationBannerEnabled = toggle(
      DEAL_FREEZE_EDUCATION_BANNER_EXPERIMENT,
      false,
      true
    )
    if (!isEducationBannerEnabled) return

    setImpression({placement, searchId, isVisible: true})
    dispatch(trackDealFreezeEducationBannerDisplayed({placement}))
  }, [dispatch, placement, searchId, toggle])

  useEffect(() => {
    if (!isImpressionVisible) return
    if (
      isStaleForGha ||
      !isFreezableFilterAvailable ||
      isDealFreezeFilterApplied ||
      (isSearchCompleted && (!hasResults || greatDeals.length === 0))
    ) {
      hide()
    }
  }, [
    isImpressionVisible,
    isStaleForGha,
    isFreezableFilterAvailable,
    isDealFreezeFilterApplied,
    isSearchCompleted,
    hasResults,
    greatDeals.length,
    hide
  ])

  const dismiss = useCallback(() => {
    if (!impressionPlacement) return

    hide()
    dispatch(
      trackDealFreezeEducationBannerDismissed({placement: impressionPlacement})
    )
  }, [dispatch, hide, impressionPlacement])

  const onCtaClick = useCallback(() => {
    setDealFreezeEducationBannerCtaClicked()
    dispatch(
      trackDealFreezeEducationBannerCtaClicked({
        placement: impressionPlacement ?? placement
      })
    )
    submitFilters({dealFreeze: '1' as BooleanUrlParam})
    hide()
  }, [dispatch, hide, impressionPlacement, placement, submitFilters])

  const value = useMemo(
    () => ({
      placement,
      isTriggerable: !hasStarted && !isAuthLoading && isEligible,
      visiblePlacement,
      startImpression,
      dismiss,
      onCtaClick
    }),
    [
      placement,
      hasStarted,
      isAuthLoading,
      isEligible,
      visiblePlacement,
      startImpression,
      dismiss,
      onCtaClick
    ]
  )

  return (
    <DealFreezeEducationBannerContext.Provider value={value}>
      {children}
    </DealFreezeEducationBannerContext.Provider>
  )
}
